import MessageBar from "./components/message-bar";
import MessageList from "./components/message-list";
import Topbar from "./components/topbar";
import styles from "./chat.module.css";
import { StoredChat } from "../../../../../../lib/chat-store/types";
import { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import UploadOverlay from "./components/upload-overlay";
import UploadDialog from "./components/upload-dialog";
import {
  getCurrentCategory,
  handleFilesInput,
  resolveMediaCategory,
  useUploadStore,
  validateAndAddFiles,
} from "../../upload-store";
import { useChatStore } from "../../../../../../lib/chat-store/chats-store";
import { toast } from "react-toastify";
import { useMediaPolling } from "../../useMediaPooling";

export const MAX_FILE_SIZE = 100 * 1024 * 1024; // 50MB
export const MAX_FILES = 10;

export default function ChatComponent({ chatData }: { chatData: StoredChat }) {
  const { uploads, addFiles, removeFile, clear, getUploadedMediaIds } =
    useUploadStore();
  const [activeIndex, setActiveIndex] = useState(0);
  const [caption, setCaption] = useState("");
  const { sendMessage } = useChatStore();
  const updateMessages = useChatStore((s) => s.updateMessages);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: true,
    noClick: true,
    noKeyboard: true,
    maxFiles: 10,
    maxSize: MAX_FILE_SIZE,
    accept: {
      "image/*": [],
      "video/*": [],
      "application/pdf": [],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
    },
    onDrop: (acceptedFiles, rejectedFiles) => {
      // handle rejected
      if (rejectedFiles.length) {
        rejectedFiles.forEach((file) => {
          const err = file.errors[0];
          showUploadError(mapDropzoneError(err.code));
        });
      }

      handleFilesInput(acceptedFiles, uploads, addFiles);
    },
  });
  // 🧠 Clear uploads when switching chat
  useEffect(() => {
    clear();
    setCaption("");
    setActiveIndex(0);
  }, [chatData.conversation_id, clear]);
  // 🧠 Fix activeIndex when uploads change
  useEffect(() => {
    if (activeIndex >= uploads.length) {
      setActiveIndex(Math.max(0, uploads.length - 1));
    }
  }, [uploads.length, activeIndex]);

  useMediaPolling(chatData.messages, chatData.conversation_id, updateMessages);

  return (
    <div {...getRootProps()} className={styles.chatWrapper}>
      <input {...getInputProps()} />
      <Topbar chatData={chatData} />
      <MessageList
        messages={chatData.messages}
        isAssistantTyping={chatData.is_assistant_typing}
        test_image_url={chatData.receiver_photo_url}
      />
      {isDragActive && <UploadOverlay />}

      {uploads.length > 0 && (
        <UploadDialog
          uploads={uploads}
          activeIndex={activeIndex}
          setActiveIndex={setActiveIndex}
          caption={caption}
          setCaption={setCaption}
          onRemove={(id) => {
            removeFile(id);
          }}
          onCancel={() => {
            clear();
            setCaption("");
            setActiveIndex(0);
          }}
          onSend={() => {
            const mediaIds = getUploadedMediaIds();

            if (mediaIds.length === 0) {
              alert("No files uploaded yet");
              return;
            }

            sendMessage(
              chatData.conversation_id,
              "media",
              {
                text: caption,
                media_ids: mediaIds,
              },
              chatData.receiver_id,
            );

            clear();
            setCaption("");
            setActiveIndex(0);
          }}
        />
      )}

      {/* <ListingSelectionDialog
        listings={[]}
        selected={[]}
        setSelected={() => {}}
        activeIndex={0}
        setActiveIndex={() => {}}
        caption=""
        onCancel={() => {}}
        onSend={() => {}}
        setCaption={() => {}}
      /> */}

      <MessageBar chatData={chatData} />
    </div>
  );
}

function mapDropzoneError(code: string) {
  switch (code) {
    case "file-too-large":
      return "too-large-err";
    case "file-invalid-type":
      return "invalid-type-err";
    case "too-many-files":
      return "too-many-err";
    case "different-categories":
      return "different-categories-err";
    default:
      return "invalid-err";
  }
}

export function showUploadError(msg: string) {
  toast(msg, { type: "error" });
}
